import React, { FC, useRef } from 'react'
import _ from 'lodash'

import CollapsibleForm from '../../../CollapsibleForm'
import { SettingsPopupProps } from '../../SettingsPopup'
import { uiSchema } from './permissionsSchema'
import { useBotConfiguration } from '../../../../features/serverBots'
import { useSettingsPopupState } from '../../hooks'
import { ModalContent, ModalFooter, ModalFooterButton } from '../../styles'
import { endpointHelpers } from '../../../../features/serverBots/endpointHelpers'
import { useBotId } from '../../../../store/flow'
import { injectAccessTokensWhenMissing, wasNewPaidUserAdded } from './utils'
import { promptToConfirmAddingNewPaidUser } from '../../../Step/utils/promptToConfirmAddingNewPaidUser'
import { useFeature } from '../../../../store/features'
import { useSchema } from './hooks'

export const BotPermissionsForm: FC<SettingsPopupProps> = props => {
  const botId = useBotId()
  const config = useBotConfiguration()
  const schema = useSchema()
  const tokenMaxRole = useFeature('SHARE_TOKEN_MAX_ROLE')

  const [triggerRequest, requestState] = endpointHelpers.updateBotConfiguration.useMutation()

  const submitButtonRef = useRef<HTMLButtonElement>(null)

  const { formData, initialFormData, onFormDataChange } = useSettingsPopupState(
    () => config?.permissions && _.pick(config.permissions, Object.keys(schema.properties))
  )

  if (!config) {
    return null
  }

  return (
    <>
      <ModalContent>
        <CollapsibleForm
          schema={schema}
          uiSchema={uiSchema as any}
          formData={formData}
          className={props.className}
          liveValidate={true}
          onChange={(newFormData, isInitialPopulation) => {
            const adjustedFormData = injectAccessTokensWhenMissing(newFormData as any)
            onFormDataChange(adjustedFormData, isInitialPopulation)
          }}
          submitButtonRef={submitButtonRef}
          onSubmit={async ({ formData }) => {
            if (wasNewPaidUserAdded(initialFormData, formData)) {
              if (!(await promptToConfirmAddingNewPaidUser())) {
                return
              }
            }

            triggerRequest({ id: botId, configurationPatch: { permissions: formData } })
          }}
          validateSchema={(formData, errors) => {
            const permittedRoles = schema.properties.tokens.items.properties.role.enum

            const hasTokenWithUnpermittedRole = formData?.tokens?.some(
              (token: any) => !permittedRoles.includes(token.role)
            )

            if (hasTokenWithUnpermittedRole) {
              // Attaching the error at any lower level (i.e. to a specific token) DOES NOT DISPLAY out of the box
              // so don't burn your time, unless you care a lot :)
              errors.addError(
                `Access tokens can no longer carry a role higher than "${tokenMaxRole}". Please, select a different role or contact your administrator to modify this restriction`
              )
            }

            return errors
          }}
        />
      </ModalContent>

      <ModalFooter>
        <ModalFooterButton
          label="Close"
          className="e2e-close-bot-modal-button"
          onClick={props.close}
        />

        <ModalFooterButton
          emphasis="primary"
          disabled={requestState.isLoading}
          loading={requestState.isLoading}
          label="Apply now"
          className="e2e-modal-success-button"
          onClick={() => submitButtonRef.current?.click?.()}
        />
      </ModalFooter>
    </>
  )
}
